import React from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import Reveal from "../components/site/Reveal";
import { PORTRAIT, BOOKING_URL, IMAGES } from "../lib/site";
import { ArrowUpRight } from "lucide-react";
import { useSeo, personSchema } from "../lib/seo";

const EASE = [0.22, 1, 0.36, 1];

const containerStagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const lineMask = {
  hidden: { y: "110%" },
  visible: {
    y: "0%",
    transition: { duration: 0.9, ease: EASE },
  },
};

const softRise = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.0, ease: EASE },
  },
};

export default function About() {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const heroImgY = useTransform(scrollYProgress, [0, 0.25], ["0%", "10%"]);
  const heroImgScale = useTransform(scrollYProgress, [0, 0.25], [1.02, 1.06]);

  useSeo({
    title: "About Dr. Nausheen Mirza. General Dentist & Founder of Alae Dental, Vaughan",
    description:
      "Meet Dr. Nausheen Mirza, a Toronto-born General Dentist and founder of Alae Dental in Vaughan. NYU trained, with over 13 years of clinical experience in Canada and the United States.",
    path: "/about",
    jsonLd: personSchema(),
    jsonLdId: "about-jsonld",
  });

  return (
    <main className="bg-linen text-umber">
      {/* Kinetic Hero */}
      <section className="relative overflow-hidden paper-grain min-h-[70svh] flex items-end pt-24 md:pt-32 pb-16 md:pb-24 bg-paper">
        {/* Soft background image + parallax */}
        <motion.div
          className="absolute inset-0 opacity-40"
          style={reduce ? undefined : { y: heroImgY, scale: heroImgScale }}
          initial={reduce ? false : { scale: 1.1, opacity: 0 }}
          animate={reduce ? undefined : { scale: 1.02, opacity: 0.4 }}
          transition={{ duration: 1.6, ease: EASE }}
        >
          <img
            src={IMAGES.philosophy}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="sync"
            className="absolute inset-0 w-full h-[115%] object-cover"
            style={{ filter: "saturate(0.85) contrast(0.95)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-paper/60 via-paper/30 to-paper/80" />
        </motion.div>

        <motion.div
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full"
          variants={containerStagger}
          initial="hidden"
          animate="visible"
        >
          {/* Meta row with animated hairline */}
          <div className="flex items-center gap-4 mb-6">
            <motion.div
              className="h-[1px] w-10 md:w-14 bg-forest origin-left"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
            />
            <motion.div variants={softRise} className="font-meta text-forest">
              About the Founder
            </motion.div>
          </div>

          {/* Masked line-by-line headline */}
          <h1 className="font-display text-5xl md:text-6xl lg:text-[88px] leading-[1.02] tracking-tight text-umber max-w-5xl">
            <span className="block overflow-hidden">
              <motion.span variants={lineMask} className="block">
                Meet Dr. Nausheen
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span variants={lineMask} className="block">
                <span className="font-display-italic text-forest">Mirza.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={softRise}
            className="mt-8 font-meta text-umber/70"
          >
            General Dentist & Founder, Alae Dental
          </motion.p>
        </motion.div>
      </section>

      {/* Body */}
      <section className="py-20 md:py-28 paper-grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative max-w-[300px] sm:max-w-[340px] mx-auto lg:max-w-none">
                {/* Framed accent blocks behind portrait */}
                <div
                  aria-hidden="true"
                  className="absolute -left-4 -top-4 md:-left-6 md:-top-6 w-24 h-24 md:w-40 md:h-40 border border-forest/50"
                />
                <div
                  aria-hidden="true"
                  className="absolute -right-3 -bottom-3 md:-right-5 md:-bottom-5 w-16 h-16 md:w-28 md:h-28 bg-forest"
                />
                {/* Portrait with spotlight overlay */}
                <div className="relative aspect-[4/5] overflow-hidden shadow-paper">
                  <motion.img
                    initial={{ scale: 1.15, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 1.4, ease: EASE }}
                    src={PORTRAIT.drMirza}
                    alt={PORTRAIT.drMirzaAlt}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center 20%" }}
                  />
                  {/* Diagonal reveal wipe */}
                  <motion.div
                    aria-hidden="true"
                    initial={{ scaleY: 1 }}
                    whileInView={{ scaleY: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 1.2, ease: EASE, delay: 0.1 }}
                    className="absolute inset-0 bg-linen origin-bottom"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse at 50% 30%, transparent 45%, rgba(28,56,41,0.15) 100%)",
                    }}
                  />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pl-10">
            <Reveal>
              <p className="text-base md:text-lg leading-relaxed text-umber/85">
                Dr. Nausheen Mirza is a Toronto-born General Dentist with over
                13 years of clinical experience across Canada and the United
                States. Raised in Woodbridge from the age of two, she returned
                home to bring her vision for Alae Dental to life and serve the
                community she has always called home.
              </p>
            </Reveal>

            <Reveal delay={150}>
              <blockquote className="mt-12 border-l-2 border-forest pl-6 md:pl-8">
                <p className="font-display-italic text-2xl md:text-3xl leading-snug text-umber">
                  &ldquo;After years of practicing and teaching, I knew exactly
                  what I wanted to create. A practice where I could deliver
                  dentistry the way I believe it should be practiced, with the
                  care and attention every patient deserves.&rdquo;
                </p>
                <footer className="font-meta text-umber/70 mt-6">
                  Dr. Nausheen Mirza
                </footer>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="bg-paper paper-grain py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <h2 className="font-meta text-forest mb-6">
              Education & Training
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-umber max-w-3xl">
              A foundation built through years of study and training.
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-umber/85 max-w-3xl">
              Dr. Mirza completed her Honours Bachelor of Science in Biology at
              York University before earning her Doctor of Dental Surgery from
              New York University College of Dentistry in 2013. She later
              completed an Implant Fellowship through the University of Florida
              and has pursued continuing education through Spear Education and
              the Las Vegas Institute for Advanced Dental Studies (LVI).
            </p>
          </Reveal>

          <div className="mt-12 border-t border-umber/20">
            {[
              {
                title: "Implant Fellowship",
                place: "University of Florida",
              },
              {
                title: "Advanced Continuing Education",
                place: "Spear Education & the Las Vegas Institute for Advanced Dental Studies (LVI)",
              },
            ].map((it, i) => (
              <Reveal key={it.title} delay={i * 100}>
                <div className="border-b border-umber/20 py-7 grid grid-cols-12 gap-4">
                  <div className="col-span-2 font-meta text-forest">
                    0{i + 1}
                  </div>
                  <div className="col-span-10 md:col-span-5 font-display text-2xl text-umber">
                    {it.title}
                  </div>
                  <div className="col-span-12 md:col-span-5 text-umber/80">
                    {it.place}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Career */}
      <section className="py-20 md:py-28 paper-grain">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <h2 className="font-meta text-forest mb-6">
              Clinical Experience & Education
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-base md:text-lg leading-relaxed text-umber/85">
              Throughout her career, Dr. Mirza has practiced in both private and
              corporate settings in Canada and the United States. She also
              served for many years as a Clinical Instructor at the University
              of Toronto Faculty of Dentistry, contributing to the clinical
              education of future dentists. While practicing in Tampa, Florida,
              Dr. Mirza was recognized as an Invisalign Gold Provider and was
              voted one of &ldquo;Tampa's Top Dentists&rdquo; by Tampa Style
              Magazine.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Vision */}
      <section className="bg-paper paper-grain py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <h2 className="font-meta text-forest mb-6">
              The Vision Behind Alae Dental
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-base md:text-lg leading-relaxed text-umber/85">
              Known for her calm demeanor, gentle approach, and attention to
              detail, Dr. Mirza believes dental care should never feel rushed or
              impersonal. Her years in clinical practice, education, and
              advanced training shaped the vision behind Alae Dental, a modern,
              relationship centred practice grounded in trust, continuity, and
              personalized care. Her approach combines modern dentistry and
              technology with clear communication and individualized attention.
              She takes the time to understand each patient's needs and goals,
              explain their options, and help them feel comfortable and informed
              throughout their care.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-12 bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-all duration-300"
            >
              Book an Appointment <ArrowUpRight size={16} />
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
